import React, { useEffect, useMemo } from 'react'
import { FaExclamationTriangle } from 'react-icons/fa'
import {
  Dictionary,
  FundingScheduleLabels,
  Installer,
  LoggedUpload,
  StatusData,
  dollars,
} from '@oneethos/shared'
import './project-list-item.scss'
import api from '../api-client'
import { toast } from 'react-toastify'
import { useAppState } from '../hooks'
import { Assignees } from './assignees'
import { InstallersStatusConfig } from '../reducers/installersStatusReducer'
import { GrTest } from 'react-icons/gr'
import { ProjectButtonTabs } from './project-button-tabs'
import { CreditReports } from '../pages/creditReportsPage'
import { ChecklistItems } from './checklist-items'
import { FaBuilding, FaHandHoldingHeart, FaHouseChimney } from 'react-icons/fa6'
import { ProjectActions } from './project-actions'

export type NavOpt = 'history' | 'checklist' | 'other' | 'changeOrder' | 'loanDetails'

type ProjectListItemProps = {
  project: StatusData & { hasUpload?: LoggedUpload }
  installersMap?: Dictionary<Installer>
  initialNav?: NavOpt
  onChange: (project: StatusData) => void
  onDoneEditingAssignees?: () => void
  showDetail?: boolean
  isExpanded?: boolean
}

type InstallersStatusProps = {
  project: StatusData
  installersStatusConfig: InstallersStatusConfig
  onChange: (project: StatusData) => void
}

const projectTypeIcons = {
  'residential': <FaHouseChimney size='15' />,
  'commercial': <FaBuilding size='15' />,
  'non-profit': <FaHandHoldingHeart size='15' />,
}

const InstallersStatus = ({ project, installersStatusConfig, onChange }: InstallersStatusProps) => {
  const statuses = new Set([
    ...installersStatusConfig.list.map(sc => sc.status),
    project.installersStatus
  ])

  const isChangeOrderPendingStatus = project.installersStatus === 'Change Order Pending'

  useEffect(() => {
    if (isChangeOrderPendingStatus) {
      toast('Please note that funding cannot be requested during a change order.', {
        autoClose: false,
        closeOnClick: false,
        draggable: false,
        closeButton: true,
        theme: 'colored',
        icon: <FaExclamationTriangle style={{ color: '#664d03' }} />,
        className: 'change-order-toast',
      })
    }
  }, [isChangeOrderPendingStatus])

  const status = isChangeOrderPendingStatus ? 
    <span className="badge text-bg-warning text-uppercase"> <FaExclamationTriangle/> {project.installersStatus}</span> : 
    <span>{project.installersStatus}</span>

  return <div className="pr-info"><b>Status: </b>
    {process.env.REACT_APP_NODE_ENV === 'test' ? <div className="d-inline-flex">
      <div className="d-flex align-items-center">
        <select className="form-select" value={project.installersStatus} onChange={e => {
          api.patch(`/loanapps/${project.id}`, {
            installersStatus: e.target.value
          }).then(data => {
            toast.success('saved status')
            onChange(new StatusData(data))
          }).catch(ex => toast.error(ex.error || ex.message, { autoClose: false }))
        }}>
          {Array.from(statuses).map((status, idx) => (
            <option key={idx} value={status}>{status}</option>
          ))}
        </select>
        <FaExclamationTriangle className="ms-1" title="Only editable in test; manual changes may have undefined side effects" style={{ color: 'var(--oe-yellow)' }} />
        <GrTest title="Test Only" className="ms-1" />
      </div>
    </div> : status}
  </div>
}

const DefaultProjectContent = ({
  project,
  onChange,
  isExpanded
}) => {
  const { installersStatusConfig: isc, registration: { installer } } = useAppState()
  const { phone, email, loanAmount, appId, fundingSchedule, equipmentType } = project

  return (
    <div className='d-flex flex-column gap-2'>
      {!isExpanded ?
        <>
          <span>
            <b>Amount:</b> {dollars(loanAmount)}
          </span>
        </>
        : <>
          <span>
            <b>Email:</b> {email}
          </span>
          {phone &&
            <span>
              <b>Phone:</b> {phone}
            </span>
          }
        </>}
      {(installer.isSupport && appId) ?
        <div>
          <b>App Id:</b> {appId}
        </div> : null
      }
      <InstallersStatus
        project={project}
        installersStatusConfig={isc}
        onChange={onChange}
      />
      <div className="text-capitalize">
        <b>Equipment Type:</b> {equipmentType.replace(/-/g, ' ')}
      </div>
      {fundingSchedule &&
        <div>
          <b>Funding Schedule:</b> {FundingScheduleLabels[fundingSchedule]}
        </div>}
    </div>
  )
}

export const ProjectListItem = ({
  project,
  installersMap,
  initialNav,
  onChange,
  onDoneEditingAssignees,
  showDetail,
  isExpanded,
}: ProjectListItemProps) => {
  const { registration: { installer }, installersStatusConfig: isc } = useAppState()
  const crSearch = useMemo(() => new URLSearchParams({ loanAppId: project.id }), [project.id])
  const { email, customerName, businessName, projectType, address, phone } = project

  const doneEditingAssignees = () => {
    api.patch(`/loanapps/${project.id}`, {
      authorizedInstallers: project.authorizedInstallers
    }).then(() => {
      toast.success('saved assignees')
      if (onDoneEditingAssignees) onDoneEditingAssignees()
    }).catch(ex => toast.error(ex.error || ex.message, { autoClose: false }))
  }

  return (
    <div className={`${isExpanded ? '' : 'm-3'}`}>
      {isExpanded ? null :
        <div className='project-header container-md'>
          <div className='d-flex align-items-center gap-2'>
            <div>{projectTypeIcons[projectType]}</div>
            <div>{customerName || businessName || 'Referred'}</div>
          </div>
          <div >{email || 'null'}</div>
          <div>{phone}</div>
          <div>{address}</div>
        </div>
      }
      <div className={`container-md project-content ${isExpanded ? '' : 'rounded-bottom-4 shadow-sm'}`}>
        {project.error ? <div className="alert alert-danger">
          <b>ERROR: {project.error}</b>
          <pre className="mt-2">Submission Response: {JSON.stringify(project.submissionResponse, null, 2)}</pre>
        </div> : null}
        <DefaultProjectContent
          project={project}
          onChange={onChange}
          isExpanded={isExpanded}
        />
        <ChecklistItems
          project={project}
          onChange={onChange}
        />
        <ProjectActions
          project={project}
          installer={installer}
          onChange={onChange}
        />
        {installer.isAdmin ? <Assignees
          project={project}
          installersMap={installersMap}
          onChange={onChange}
          onDoneEditing={doneEditingAssignees}
        /> : null}
        <ProjectButtonTabs
          project={project}
          onChange={onChange}
        />
        {project.isPrequalified && showDetail && installer.isSupport ?
          <CreditReports search={crSearch} />
          : null}
      </div>
    </div>
  )
}

export default ProjectListItem
